// Taşeron portalı — Belge Analizi gelen kutusu: T.C.'si opsiyonel, iletişimsiz personel yakalama
// modalı (plan S25 C9, `docs/plans/S25-analysis-inbox.md`). `createSupplierPersonnel` →
// `POST /supplier/contractor/personnel` AYNI ucun iletişimsiz yakalama yoludur (GL6 §4.2: SP'de
// T.C. yalnız e-posta/telefon verilince zorunludur); bu modal İLETİŞİM GÖNDERMEZ, dolayısıyla
// `link` daima `null`'dır ve kayıt ANINDA `kind: Personnel, status: NoLogin` olur — belge
// kabulüne HEMEN hedef gösterilebilir. "Personel oluştur" çekmecesi `PersonnelCreateSheet`'tir.
// Aynı kişiye sonradan aktivasyon bağlantısı göndermek AYRI bir eylemdir (Personelim ekranından).
//
// **Ön-doldurma.** `prefill.fullName`/`prefill.tcKimlikNo` belgeden çıkarılan GÜVENİLMEZ
// metindir (KVKK/S17-D8) — yalnız DÜZENLENEBİLİR form alanı DEĞERİ olarak kullanılır, URL'ye/
// loga/`title`'a asla girmez. Ad/soyad bölünmesi bir SEZGİDİR (son boşluktan sonrası soyad) —
// ikisi de zorunlu alan olduğu için kullanıcı göndermeden önce gözden geçirir/düzeltir.
//
// **TC doğrulama istemcide `lib/tcKimlik.ts` ile — backend `TcKimlikValidator`la BİREBİR aynı
// algoritma** (yalnız UX için; sunucu her koşulda kendi kuralını yeniden uygular). Sunucu
// hataları alan bazında eşlenir: `TC_KIMLIK_INVALID` / `PERSONNEL_TC_ALREADY_EXISTS` → `tcKimlikNo`
// alanı; diğerleri genel `Alert`.
//
// **Kapanışta odak.** `Modal`ın `Dialog.Trigger`ı yok (`useReturnFocus.ts` dosya üstü notu) —
// çağıran (`AnalysisReviewPane`) İPTAL/Esc/dış tıkta tetikleyiciye döner, BAŞARILI oluşturmada
// bunu KASITLI OLARAK atlar (yeni kişiyi seçip Kişi tetikleyicisine odaklanır) — `ConfirmDialog`ın
// `justDecidedRef` deseniyle AYNI.

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  type ContractorPersonnelDto,
  Field,
  Input,
  Modal,
  isApiError,
  isValidTcKimlikNo,
  resolveApiErrorMessage,
  supplierContractorApi,
} from '@isgportal/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { supplierContractorKeys } from '../lib/supplierContractorKeys';

export interface PersonnelCreatePrefill {
  /** GÜVENİLMEZ metin (KVKK) — yalnız form alanı ön-doldurması. */
  fullName: string | null;
  /** GÜVENİLMEZ metin (KVKK) — yalnız form alanı ön-doldurması. */
  tcKimlikNo: string | null;
}

interface PersonnelCreateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: number | null;
  prefill: PersonnelCreatePrefill;
  onCreated: (person: ContractorPersonnelDto) => void;
}

/** Ad/soyad bölme SEZGİSİ — son boşluktan sonrası soyad. Tek token varsa tamamı ad, soyad boş. */
function splitFullName(fullName: string | null): { firstName: string; lastName: string } {
  const trimmed = (fullName ?? '').trim();
  if (trimmed.length === 0) {
    return { firstName: '', lastName: '' };
  }
  const lastSpace = trimmed.lastIndexOf(' ');
  if (lastSpace === -1) {
    return { firstName: trimmed, lastName: '' };
  }
  return { firstName: trimmed.slice(0, lastSpace).trim(), lastName: trimmed.slice(lastSpace + 1) };
}

export function PersonnelCreateModal({
  open,
  onOpenChange,
  companyId,
  prefill,
  onCreated,
}: PersonnelCreateModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const schema = useMemo(
    () =>
      z.object({
        firstName: z.string().trim().min(1, t('personnel.create.validation.firstNameRequired')),
        lastName: z.string().trim().min(1, t('personnel.create.validation.lastNameRequired')),
        tcKimlikNo: z
          .string()
          .trim()
          .refine(
            (v) => v.length === 0 || isValidTcKimlikNo(v),
            t('personnel.create.validation.tcInvalid'),
          ),
      }),
    [t],
  );
  type FormValues = z.infer<typeof schema>;

  // `useForm`in `defaultValues`'ı YALNIZ ilk mount'ta okunur (RHF'in kendi davranışı) — bu
  // bileşen zaten kalem başına REMOUNT olan `AnalysisReviewPaneContent`'in çocuğu, yani `prefill`
  // her mount'ta güncel gelir; `useMemo` GEREKMEZ. `reset(defaultValues)` (kapanışta) AYNI
  // hesaplamayı kapanış anındaki (değişmemiş) `prefill`le tekrarlar.
  const { firstName: prefillFirstName, lastName: prefillLastName } = splitFullName(
    prefill.fullName,
  );
  const defaultValues = {
    firstName: prefillFirstName,
    lastName: prefillLastName,
    // Yalnız rakam bırakılır — OCR ara boşluk/ayraç ekleyebilir, doğrulama saf 11 hane bekler.
    tcKimlikNo: (prefill.tcKimlikNo ?? '').replace(/\D/g, ''),
  };

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      supplierContractorApi.createSupplierPersonnel({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        tcKimlikNo: values.tcKimlikNo.trim().length > 0 ? values.tcKimlikNo.trim() : null,
      }),
    onSuccess: (result) => {
      const created = result.personnel;
      // Yeni kayıt ANINDA seçilebilir olmalı — arka planda kanonik listeyi tazelerken önbelleğe
      // hemen ekleriz (ProfilePage.tsx `setQueryData` emsali): PersonSelect'i besleyen SORGU
      // (`AnalysisReviewPane`/`AnalysisInboxRail` AYNI anahtarı okur) bu satırla ANINDA görür.
      queryClient.setQueryData<ContractorPersonnelDto[]>(
        supplierContractorKeys.personnel(companyId),
        (prev) => (prev ? [...prev, created] : [created]),
      );
      queryClient.invalidateQueries({ queryKey: supplierContractorKeys.personnel(companyId) });
      reset(defaultValues);
      onCreated(created);
    },
    onError: (error) => {
      if (isApiError(error)) {
        if (error.errorCode === 'TC_KIMLIK_INVALID') {
          setError('tcKimlikNo', {
            message: t('TC_KIMLIK_INVALID', {
              defaultValue: t('personnel.create.validation.tcInvalid'),
            }),
          });
          return;
        }
        if (error.errorCode === 'PERSONNEL_TC_ALREADY_EXISTS') {
          setError('tcKimlikNo', {
            message: t('PERSONNEL_TC_ALREADY_EXISTS', {
              defaultValue: t('personnel.create.tcExistsFallback'),
            }),
          });
          return;
        }
      }
    },
  });

  const submitting = mutation.isPending;
  const generalError =
    mutation.isError &&
    !(
      isApiError(mutation.error) &&
      (mutation.error.errorCode === 'TC_KIMLIK_INVALID' ||
        mutation.error.errorCode === 'PERSONNEL_TC_ALREADY_EXISTS')
    )
      ? resolveApiErrorMessage(mutation.error, t)
      : null;

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          reset(defaultValues);
          mutation.reset();
        }
        onOpenChange(next);
      }}
      title={t('personnel.create.title')}
      description={t('personnel.create.description')}
      size="sm"
      closeLabel={t('common.close')}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            form="supplier-personnel-create-form"
            loading={submitting}
            disabled={submitting}
          >
            {t('personnel.create.submit')}
          </Button>
        </>
      }
    >
      <form
        id="supplier-personnel-create-form"
        className="flex flex-col gap-4"
        onSubmit={handleSubmit((values) => mutation.mutate(values))}
        noValidate
      >
        {generalError ? <Alert tone="danger">{generalError}</Alert> : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            htmlFor="personnel-create-firstName"
            label={t('personnel.create.field.firstName')}
            error={errors.firstName?.message}
            required
          >
            <Input
              id="personnel-create-firstName"
              autoComplete="given-name"
              invalid={Boolean(errors.firstName)}
              {...register('firstName')}
            />
          </Field>
          <Field
            htmlFor="personnel-create-lastName"
            label={t('personnel.create.field.lastName')}
            error={errors.lastName?.message}
            required
          >
            <Input
              id="personnel-create-lastName"
              autoComplete="family-name"
              invalid={Boolean(errors.lastName)}
              {...register('lastName')}
            />
          </Field>
        </div>

        <Field
          htmlFor="personnel-create-tc"
          label={`${t('personnel.create.field.tcKimlikNo')} (${t('personnel.invite.optional')})`}
          hint={t('personnel.create.field.tcKimlikNoHint')}
          error={errors.tcKimlikNo?.message}
        >
          <Input
            id="personnel-create-tc"
            inputMode="numeric"
            maxLength={11}
            autoComplete="off"
            invalid={Boolean(errors.tcKimlikNo)}
            {...register('tcKimlikNo')}
          />
        </Field>
      </form>
    </Modal>
  );
}
